<script lang="ts" setup>
  import { computed, ref, watch } from 'vue'

  import { useTheme } from 'vuetify'

  import { useServiceLaunch } from '#ourahub-intra/composables/serviceLaunch'

  import { useAppStore } from '#ourahub-intra/stores/application'
  import { useServicesStore } from '#ourahub-intra/stores/services'

  import { useOcpStatusQuery } from '#ourahub-intra/queries/ocp'

  import { STAGE_VERSION_NAMES } from '#ourahub-intra/constants'

  import type { Service } from '#ourahub-intra/types/services'
  import type { AppEnvironment } from '#web-oura-tools/types/auth'
  import type { OuraUser } from '#web-oura-tools/types/user'

  const props = defineProps<{
    user: OuraUser
    pinned: boolean
    service: Service
  }>()

  const theme = useTheme()

  const ouraHubStore = useAppStore()
  const servicesStore = useServicesStore()

  const hasUpdates = ref(false)
  const serviceDialog = ref(false)

  const { launchService } = useServiceLaunch()

  const { data: statusData } = useOcpStatusQuery(props.service.id)

  const category = computed(() => servicesStore.getCategory(props.service))

  const publicStage = computed(() => servicesStore.publicStage(props.service))
  const launchStages = computed(() => servicesStore.launchStages(props.service))

  const launchText = computed(() => {
    if (!launchStages.value.some((stage) => stage === publicStage.value)) {
      return props.service.context === 'web-service-ssr'
        ? 'Test in new tab'
        : props.service.context === 'web-service-spa'
          ? 'Test application'
          : 'Not supported yet'
    } else {
      return props.service.context === 'web-service-ssr'
        ? 'Open in new tab'
        : props.service.context === 'web-service-spa'
          ? 'Launch application'
          : 'Not supported yet'
    }
  })

  const notRunning = computed(() => {
    if (!statusData?.value?.resources) {
      return false
    }

    const appStatus = statusData?.value?.resources.find((r) => r.kind === 'OuraHubApp')

    return !appStatus?.workloads?.every((w) => w.ok)
  })

  watch(
    () => ouraHubStore.serviceVersions,
    (versions) => {
      if (versions) {
        const latestVersion = props.service.version

        hasUpdates.value = !!latestVersion && latestVersion !== versions[props.service.id]
      }
    },
    { immediate: true },
  )

  async function navigateToService(service: Service, stage: AppEnvironment) {
    await launchService(service, stage)
  }
</script>

<template lang="html">
  <v-hover>
    <template #default="{ props: hoverProps }">
      <v-card
        v-bind="hoverProps"
        flat
        border
        width="380"
        rounded="lg"
        class="d-flex flex-column ma-2"
        :data-intro-tour-id="service.id === 'outo-proto-creator' ? 'proto-creator-card' : undefined"
      >
        <v-card-title class="d-flex flex-row align-center py-0 mb-n3">
          <h5 class="text-body-medium text-grey">{{ service.category.toUpperCase() }}</h5>

          <v-spacer />

          <v-btn
            elevation="0"
            class="ml-4 mr-n4 mt-n2 mb-n1"
            :icon="pinned ? 'mdi-pin' : 'mdi-pin-outline'"
            :class="
              props.pinned
                ? 'text-deep-orange'
                : theme.global.name.value === 'dark'
                  ? 'text-blue-grey-lighten-3'
                  : 'text-blue-grey-darken-3'
            "
            @click.stop="servicesStore.togglePinnedService(props.service)"
          />
        </v-card-title>

        <v-card-text class="d-flex flex-column flex-grow-1">
          <v-row class="flex-grow-0 flex-shrink-1">
            <v-icon size="large" class="mr-n4" :icon="service.icon" :color="category?.color ?? 'grey'" />

            <h2 class="text-title-large font-weight-bold my-0">
              {{ service.name }}
            </h2>
          </v-row>

          <div style="min-height: 0" class="pt-4 flex-grow-0 flex-shrink-1 font-weight-light pl-1">
            {{ service.summary }}
          </div>

          <v-spacer />

          <v-row class="pt-3 flex-grow-0 flex-shrink-1">
            <o-chip
              v-if="notRunning"
              class="mr-n3"
              color="error"
              text="Not running"
              prepend-icon="mdi-cloud-off-outline"
            />
            <o-chip
              v-else-if="!publicStage"
              class="mr-n3"
              color="warning"
              text="Under development"
              prepend-icon="mdi-account-hard-hat-outline"
            />
            <o-chip
              v-else-if="hasUpdates"
              color="info"
              class="mr-n3"
              text="New / updated"
              prepend-icon="mdi-bell-outline"
            />

            <o-chip
              v-if="servicesStore.isMaintainer(service, true)"
              text="Owner"
              color="warning"
              prepend-icon="mdi-crown"
            />
            <o-chip
              v-else-if="servicesStore.isMaintainer(service, false)"
              color="warning"
              text="Collaborator"
              prepend-icon="mdi-handshake"
            />
          </v-row>
        </v-card-text>

        <v-card-actions
          class="d-flex flex-row align-stretch pt-2"
          style="border-top: 1px solid rgba(0, 0, 0, 0.2)"
          :class="theme.global.name.value === 'dark' ? 'bg-blue-grey-darken-4' : 'bg-blue-grey-lighten-5'"
        >
          <v-btn
            class="my-n2"
            icon="mdi-information-outline"
            :color="notRunning ? 'error' : !publicStage ? 'warning' : hasUpdates ? 'info' : ''"
            :data-intro-tour-id="service.id === 'outo-proto-creator' ? 'proto-creator-info' : undefined"
            @click="serviceDialog = true"
          />

          <v-spacer />

          <v-menu v-if="launchStages.length > 1" offset="16" location="top end">
            <template #activator="{ props: menuProps }">
              <o-btn-secondary
                v-bind="menuProps"
                style="min-width: 180px"
                append-icon="mdi-menu-down"
                :text="launchText"
                :prepend-icon="servicesStore.isLaunchableInNewTab(service) ? 'mdi-open-in-new' : ''"
                :data-intro-tour-id="service.id === 'outo-proto-creator' ? 'proto-creator-launch' : undefined"
              />
            </template>

            <v-list>
              <v-list-item
                v-for="stage in launchStages"
                :key="stage"
                :prepend-icon="stage === 'release' ? 'mdi-earth' : stage === 'staging' ? 'mdi-flask' : 'mdi-dev-to'"
                @click="navigateToService(service, stage)"
              >
                <template #title>
                  <span :class="stage === publicStage ? 'font-weight-bold' : ''">
                    {{ STAGE_VERSION_NAMES[stage] }} version
                  </span>
                </template>

                <template #subtitle>
                  <span :class="stage === publicStage ? 'font-weight-bold' : ''">
                    {{ stage === publicStage ? 'Public' : 'Private' }}
                  </span>
                </template>
              </v-list-item>
            </v-list>
          </v-menu>

          <o-btn-secondary
            v-else-if="launchStages.length === 1"
            style="min-width: 180px"
            :text="launchText"
            :prepend-icon="servicesStore.isLaunchableInNewTab(service) ? 'mdi-open-in-new' : ''"
            :data-intro-tour-id="service.id === 'outo-proto-creator' ? 'proto-creator-launch' : undefined"
            @click.stop="navigateToService(service, launchStages[0])"
          />

          <o-btn-secondary
            v-else-if="!publicStage"
            readonly
            color="grey"
            variant="tonal"
            text="Not available"
            style="min-width: 180px"
            prepend-icon="mdi-information-outline"
          />

          <o-btn-secondary
            v-else-if="!servicesStore.hasAccessRights(service, publicStage)"
            readonly
            color="grey"
            variant="tonal"
            text="No access rights"
            style="min-width: 180px"
            prepend-icon="mdi-information-outline"
          />

          <o-btn-secondary
            v-else
            readonly
            color="grey"
            variant="tonal"
            style="min-width: 180px"
            text="No frontend available"
            prepend-icon="mdi-information-outline"
          />
        </v-card-actions>
      </v-card>
    </template>
  </v-hover>

  <ServiceDetails
    v-if="serviceDialog"
    :id="service.id"
    :focus-section="notRunning ? 'status' : !publicStage ? 'maintainers' : hasUpdates ? 'changelog' : undefined"
    @close="serviceDialog = false"
  />
</template>
